import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { usersAPI } from '../../lib/api';
import { fehlertext } from '../../lib/fehlertext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';

const ALLGEMEIN = 'Das hat nicht geklappt. Bitte versuchen Sie es erneut.';
const name = (u) => `${u.first_name} ${u.last_name}`;

const ARTEN = {
  sperren: {
    titel: (u) => `Konto von ${name(u)} sperren?`,
    knopf: 'Konto sperren', laedt: 'Wird gesperrt...', variante: 'destructive',
    erfolg: 'Das Konto wurde gesperrt.', fehler: ALLGEMEIN,
    aufruf: (u, grund) => usersAPI.sperren(u.id, grund),
  },
  entsperren: {
    titel: (u) => `Konto von ${name(u)} entsperren?`,
    text: () => 'Anmeldung mit dem bisherigen Passwort, 2FA bleibt eingerichtet.',
    knopf: 'Konto entsperren', laedt: 'Wird entsperrt...', variante: 'haupt',
    erfolg: 'Das Konto wurde entsperrt.', fehler: ALLGEMEIN,
    aufruf: (u) => usersAPI.entsperren(u.id),
  },
  zweifaktor: {
    titel: () => 'Zwei-Faktor-Anmeldung zurücksetzen?',
    text: (u) => <><strong>{name(u)}</strong> wird auf allen Geräten abgemeldet und kann sich danach nur mit dem Passwort anmelden, bis die Zwei-Faktor-Anmeldung neu eingerichtet ist. Ist die Pflicht eingeschaltet, muss die Person die Zwei-Faktor-Anmeldung beim nächsten Login neu einrichten.</>,
    knopf: 'Zurücksetzen', laedt: 'Wird zurückgesetzt...', variante: 'destructive',
    erfolg: 'Die Zwei-Faktor-Anmeldung wurde zurückgesetzt.', fehler: 'Das Zurücksetzen hat nicht geklappt.',
    aufruf: (u) => usersAPI.zweiFaktorZuruecksetzen(u.id),
  },
  sitzungen: {
    titel: () => 'Alle Sitzungen beenden?',
    text: (u) => <><strong>{name(u)}</strong> wird auf allen Geräten abgemeldet und muss sich neu anmelden.</>,
    knopf: 'Sitzungen beenden', laedt: 'Wird beendet...', variante: 'destructive',
    erfolg: 'Alle Sitzungen wurden beendet.', fehler: 'Die Sitzungen konnten nicht beendet werden.',
    aufruf: (u) => usersAPI.sitzungenBeenden(u.id),
  },
};

const SperrPunkte = () => (
  <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
    <li>Wird sofort auf allen Geräten abgemeldet</li>
    <li>Kann sich nicht mehr anmelden und kein neues Passwort anfordern</li>
    <li>Daten und Verlauf bleiben erhalten – Entsperren ist jederzeit möglich</li>
  </ul>
);

// Sperren, Entsperren, 2FA zurücksetzen, Sitzungen beenden. art = null -> geschlossen.
export const KontoAktionDialog = ({ art, konto, onClose, onErledigt }) => {
  const [grund, setGrund] = useState('');
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState('');
  const a = art && konto ? ARTEN[art] : null;
  const schliessen = () => { if (laedt) return; setGrund(''); setFehler(''); onClose(); };
  const ausfuehren = async () => {
    setLaedt(true);
    setFehler('');
    try {
      await a.aufruf(konto, grund);
      toast.success(a.erfolg);
      setGrund('');
      onClose();
      onErledigt();
    } catch (err) {
      setFehler(fehlertext(err, a.fehler));
    } finally {
      setLaedt(false);
    }
  };
  return (
    <Dialog open={Boolean(a)} onOpenChange={(o) => { if (!o) schliessen(); }}>
      {a && (
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid={`konto-dialog-${art}`}>
          <DialogHeader>
            <DialogTitle>{a.titel(konto)}</DialogTitle>
            <DialogDescription className={a.text ? '' : 'sr-only'}>{a.text ? a.text(konto) : 'Das Konto wird gesperrt.'}</DialogDescription>
          </DialogHeader>
          {fehler && <p className="rounded-md bg-red-50 p-2 text-sm text-red-700" role="alert" data-testid="konto-dialog-fehler">{fehler}</p>}
          {art === 'sperren' && (
            <div className="space-y-3">
              <SperrPunkte />
              <div className="space-y-1">
                <Label htmlFor="sperr-grund">Grund (optional)</Label>
                <Input id="sperr-grund" value={grund} maxLength={200} onChange={(e) => setGrund(e.target.value)} placeholder="z. B. längere Abwesenheit" data-testid="sperr-grund" />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={schliessen} disabled={laedt} data-testid="konto-dialog-abbrechen">Abbrechen</Button>
            <Button
              variant={a.variante === 'haupt' ? 'default' : 'destructive'}
              className={a.variante === 'haupt' ? 'bg-blue-800 hover:bg-blue-900' : ''}
              onClick={ausfuehren} disabled={laedt} data-testid="konto-dialog-bestaetigen"
            >
              {laedt ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{a.laedt}</> : a.knopf}
            </Button>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default KontoAktionDialog;
